import React, { useState, useEffect } from "react";
import socket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Chip,
  Box
} from "@material-ui/core";
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalHospital as DoctorIcon,
  School as TitleIcon
} from "@material-ui/icons";
import { useTitle } from "../../hooks/useTitle";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import toastError from "../../errors/toastError";
import ProfessionalTitlesStats from "../../components/ProfessionalTitlesStats";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    height: "100%",
  },
  tableContainer: {
    borderRadius: 8,
    border: "1px solid " + theme.palette.divider,
    overflow: "hidden"
  },
  headerCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.background.default,
  },
  rowExpanded: {
    backgroundColor: theme.palette.action.selected,
  }
}));

const ProfessionalTitles = () => {
  useTitle("Especialidades");
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  const [allTitles, setAllTitles] = useState([]);
  const [titles, setTitles] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fetchTitles = async () => {
      try {
        const { data } = await api.get("/professionalTitles", { 
          params: { pageNumber: 1, limit: 50 } 
        });

        setAllTitles((prev) => [...prev, ...data.titles]);

        if (!search) {
          setTitles((prev) => [...prev, ...data.titles]);
        }
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTitles();
  }, [pageNumber]);

  useEffect(() => {
    socket.on("professionalTitle", (data) => {
      if (data.action === "update" || data.action === "create") {
        setTitles((prev) => {
          const index = prev.findIndex((t) => t.id === data.title.id);
          if (index !== -1) {
            const copy = [...prev];
            copy[index] = data.title;
            return copy;
          } else {
            return [data.title, ...prev];
          }
        });
      }

      if (data.action === "delete") {
        setTitles((prev) => prev.filter((t) => t.id !== +data.titleId));
      }
    });

    return () => {
      socket.off("professionalTitle");
    };
  }, []);

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearch(value);

    if (!value) {
      setTitles(allTitles);
      return;
    }

    const filtered = allTitles.filter((t) =>
      t.title.toLowerCase().includes(value)
    );
    setTitles(filtered);
  };

  const loadMore = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Especialidades y Doctores</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper
            className={classes.mainPaper}
            variant="outlined"
            onScroll={handleScroll}
            style={{ height: "80vh" }}
          >
             <Box mb={2} display="flex" alignItems="center" color="text.secondary">
                <TitleIcon style={{ marginRight: 8 }} />
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>Listado de Especialidades</Typography>
             </Box>
             
            <div className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>Especialidad</TableCell>
                    <TableCell align="center" className={classes.headerCell}>Doctores</TableCell>
                    <TableCell align="center" className={classes.headerCell}>Ver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {titles.map((t) => (
                      <React.Fragment key={t.id}>
                        <TableRow 
                            className={expandedId === t.id ? classes.rowExpanded : ""}
                            hover
                        >
                          <TableCell style={{ fontWeight: 500 }}>{t.title}</TableCell>
                          <TableCell align="center">
                            <Chip 
                                label={t.doctorCount} 
                                size="small" 
                                color={t.doctorCount > 0 ? "primary" : "default"} 
                                variant={t.doctorCount > 0 ? "default" : "outlined"}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedId(expandedId === t.id ? null : t.id)
                              }
                              color={expandedId === t.id ? "primary" : "default"}
                            >
                              {expandedId === t.id ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {expandedId === t.id && (
                            <TableRow style={{ backgroundColor: "#f9f9f9" }}>
                              <TableCell colSpan={3} style={{ padding: 0 }}>
                                <Box p={2}>
                                    <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: "block" }}>
                                        PROFESIONALES ASIGNADOS:
                                    </Typography>
                                    {t.doctors && t.doctors.length > 0 ? (
                                        <Grid container spacing={1}>
                                            {t.doctors.map((doc) => (
                                                <Grid item key={doc.id}>
                                                    <Chip 
                                                        icon={<DoctorIcon />} 
                                                        label={doc.name} 
                                                        variant="outlined" 
                                                        style={{ backgroundColor: "#fff" }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            No hay doctores asignados.
                                        </Typography>
                                    )}
                                </Box>
                              </TableCell>
                            </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {loading && <TableRowSkeleton columns={3} />}
                  </>
                </TableBody>
              </Table>
            </div>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
            <ProfessionalTitlesStats />
        </Grid>
      </Grid>
    </MainContainer>
  );
};

export default ProfessionalTitles;